import { useCallback, useMemo, useState } from 'react';

import type {
  AttachmentCandidate,
  PendingFile,
} from './validateAttachmentBatch';

type UsePendingAttachmentsResult = {
  pendingFiles: PendingFile[];
  addPendingAttachments: (candidates: AttachmentCandidate[]) => void;
  removePendingAttachment: (uid: string) => void;
};

/**
 * Keeps track of attachments whose upload has started but has not reached the
 * form yet, so that a later selection counts them towards the limits of the
 * message. Entries are keyed by upload id and leave the ledger as soon as the
 * upload succeeds or fails.
 */
export const usePendingAttachments = (): UsePendingAttachmentsResult => {
  const [pendingAttachments, setPendingAttachments] = useState<
    Record<string, PendingFile>
  >({});

  const addPendingAttachments = useCallback(
    (candidates: AttachmentCandidate[]): void => {
      setPendingAttachments((prevPending) => ({
        ...prevPending,
        ...Object.fromEntries(
          candidates.map(({ file, uid }) => [
            uid,
            { name: file.name, size: file.size },
          ]),
        ),
      }));
    },
    [],
  );

  const removePendingAttachment = useCallback((uid: string): void => {
    setPendingAttachments((prevPending) => {
      if (!(uid in prevPending)) {
        return prevPending;
      }

      const nextPending = { ...prevPending };
      delete nextPending[uid];
      return nextPending;
    });
  }, []);

  const pendingFiles = useMemo(
    () => Object.values(pendingAttachments),
    [pendingAttachments],
  );

  return { pendingFiles, addPendingAttachments, removePendingAttachment };
};
